import React, { useState, useEffect, forwardRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Typography,
  Box,
  Snackbar,
  Alert,
  createFilterOptions,
  Grow,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import CloseButton from '../Common/CloseButton';
import {
  buildReservedTagSet,
  getSafeTagVariable,
  hasUserTagVariableReservedChars,
} from '../../Utils/tagUtils';
import { systemTagsEvent, systemTagsGlobal } from '../../constants/systemTags';
import { updateTimeeventTag } from '../../Utils/timeEventsApi';

const filterTagOptions = createFilterOptions({ limit: 100 });

const normalizeVariable = (value) => String(value || '').trim().toLowerCase();

const getTagVariable = (tag) => {
  if (typeof tag === 'string') return tag;
  return tag?.variable ?? tag?.name ?? tag?.tag_name ?? tag?.tagName ?? '';
};

const isTimeeventTagOption = (tag) => {
  const tagType = String(tag?.tag_type ?? tag?.tagType ?? '').trim().toLowerCase();
  return !tagType || tagType === 'timeevent';
};

// Utility function to validate and normalize tag objects
const validateAndNormalizeTag = (tag) => {
  if (!tag) return null;
  
  return {
    id: tag.id || `tag_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timeeventid: tag.timeeventid,
    variable: tag.variable || tag.name || `variable_${tag.id || Math.random().toString(36).substr(2, 9)}`,
    description: tag.description || '',
    reply: tag.reply || '',
    done: tag.done || false,
    icon: tag.icon || null,
    type: tag.type || 'text',
  };
};

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} />;
});

const AddTagDialog = ({ 
  open, 
  onClose, 
  onTagCreated,
  timeevent,
  availableTags = [],
  existingTags = [],
  validationLockToken = '',
  primaryColor = '#5757b0' 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // States for creating a new tag
  const [newTagVariable, setNewTagVariable] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [newTagIcon, setNewTagIcon] = useState('');
  const [createTagErrors, setCreateTagErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTag, setSelectedTag] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const reservedTagValues = useMemo(
    () => buildReservedTagSet(systemTagsEvent, systemTagsGlobal),
    []
  );
  const existingTagVariables = useMemo(
    () => new Set(existingTags.map((tag) => normalizeVariable(getTagVariable(tag))).filter(Boolean)),
    [existingTags]
  );
  const selectableTags = useMemo(() => {
    const seenVariables = new Set();
    return availableTags.filter((tag) => {
      const variable = normalizeVariable(getTagVariable(tag));
      if (
        !variable
        || !isTimeeventTagOption(tag)
        || existingTagVariables.has(variable)
        || seenVariables.has(variable)
      ) {
        return false;
      }
      seenVariables.add(variable);
      return true;
    });
  }, [availableTags, existingTagVariables]);

  // Available icons (you can expand this list)
  const availableIcons = [
    '', // No icon option
    'web.png',
    'money.svg',
    'check.svg',
    'document.svg',
    'person.svg',
    'settings.svg',
    // Add more icons here as they become available
  ];
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setNewTagVariable('');
      setNewTagDescription('');
      setNewTagValue('');
      setNewTagIcon('');
      setCreateTagErrors({});
      setSelectedTag(null);
      setIsCreating(false);
    }
  }, [open]);

  // Manage body scroll when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Store current overflow style and hide scrollbar
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      // Cleanup function to restore scroll when dialog closes
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [open]);

  const handleTagSelection = (event, value) => {
    if (typeof value === 'string') {
      setSelectedTag(null);
      setNewTagVariable(value);
      setNewTagDescription('');
      setNewTagIcon('');
      setCreateTagErrors({});
      return;
    }
    setSelectedTag(value);
    if (value) {
      setNewTagVariable(getTagVariable(value));
      setNewTagDescription(value.description || '');
      setNewTagIcon(value.icon || '');
      setNewTagValue(''); // Value should be entered by user
    } else {
      setNewTagDescription('');
      setNewTagIcon('');
    }
    
    // Clear any existing errors
    setCreateTagErrors({});
  };

  const validateForm = () => {
    const errors = {};
    const normalizedVariable = (newTagVariable || '').trim().toLowerCase();
    const isVariableAlreadyOnEvent = existingTagVariables.has(normalizedVariable);
    const isVariableUsedInProject = availableTags.some(
      (tag) => normalizeVariable(getTagVariable(tag)) === normalizedVariable
    );
    
    if (!newTagVariable?.trim()) {
      errors.variable = t('add_tag_dialog.tag_name_required') || 'Tag name is required';
    } else if (!selectedTag && hasUserTagVariableReservedChars(newTagVariable.trim())) {
      errors.variable =
        t('add_tag_dialog.tag_name_no_space_underscore') ||
        'Tag names cannot contain spaces or reserved metadata suffixes such as _reason or _coordinates.';
    }

    if (normalizedVariable && isVariableAlreadyOnEvent) {
      errors.variable =
        t('add_tag_dialog.tag_already_added_to_event') ||
        'This tag is already present on this time event';
    } else if (!selectedTag && normalizedVariable && isVariableUsedInProject) {
      errors.variable =
        t('add_tag_dialog.tag_already_used_in_project') ||
        'This tag name is already used in this project';
    }
    
    if (!newTagDescription?.trim() && !selectedTag) {
      errors.description = t('add_tag_dialog.description_required_new_tags') || 'Description is required for new tags';
    }
    
    if (!newTagValue?.trim()) {
      errors.value = t('add_tag_dialog.tag_value_required') || 'Tag value is required';
    }
    
    setCreateTagErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNewTag = async () => {
    if (isCreating) return;
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: t('add_tag_dialog.fill_required_fields') || 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    try {
      setIsCreating(true);
      const safeVariable = getSafeTagVariable(newTagVariable, reservedTagValues);

      // Create a new tag object
      const newTag = validateAndNormalizeTag({
        id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timeeventid: timeevent.id,
        variable: safeVariable,
        description: newTagDescription.trim(),
        reply: newTagValue.trim(),
        icon: newTagIcon || null,
        done: false
      });

      if (!newTag) {
        throw new Error('Failed to create valid tag object');
      }

      const response = await updateTimeeventTag(timeevent.id, newTag, {
        isNewTag: !selectedTag,
        lockToken: validationLockToken,
      });

      // Notify parent component about the new tag
      if (onTagCreated) {
        onTagCreated(response.tag || newTag);
      }

      setSnackbar({
        open: true,
        message: selectedTag
          ? (t('add_tag_dialog.tag_added_successfully') || 'Tag added successfully!')
          : (t('add_tag_dialog.new_tag_created_successfully') || 'New tag created and added successfully!'),
        severity: 'success'
      });

      // Close dialog after short delay
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (error) {
      console.error('Error creating tag:', error);
      setSnackbar({
        open: true,
        message: error.message || (t('add_tag_dialog.failed_create_tag') || 'Failed to create tag. Please try again.'),
        severity: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        disableScrollLock={false}
        scroll="paper"
        sx={{
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(87, 87, 176, 0.15)',
          },
          '& .MuiDialog-paper': {
            overflow: isMobile ? 'hidden' : 'visible',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #5757b0 0%, #6b6bce 50%, #8080d7 100%)',
            color: 'white',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            fontSize: '1.1rem',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
              pointerEvents: 'none',
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{
              mr: 2,
              bgcolor: 'rgba(255,255,255,0.25)',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.3)',
            }}>
              <AddCircleIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.15, fontSize: '1.1rem' }}>
                {t('add_tag_dialog.add_tag_to_event') || 'Add Tag to Event'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                {t('add_tag_dialog.manually_add_tag_description') || 'Manually add a tag to this time event'}
              </Typography>
            </Box>
          </Box>
          <CloseButton
            onClick={onClose}
            ariaLabel={t('common.close', 'Close')}
            tooltip={t('common.close', 'Close')}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              width: { xs: 44, sm: 32 },
              height: { xs: 44, sm: 32 },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
              zIndex: 1,
            }}
          />
        </DialogTitle>
        
        <DialogContent 
          sx={{ 
            py: 2, 
            px: { xs: 2, sm: 3 },
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ mb: 1.5, mt: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 1, 
                    color: '#2c2c54',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    '&::before': {
                      content: '""',
                      width: '3px',
                      height: '14px',
                      backgroundColor: '#5757b0',
                      borderRadius: '2px',
                      marginRight: '8px',
                    }
                  }}                >
                  {t('add_tag_dialog.tag_name') || 'Tag Name'}
                </Typography>
                <Autocomplete
                  options={selectableTags}
                  filterOptions={filterTagOptions}
                  getOptionLabel={getTagVariable}
                  isOptionEqualToValue={(option, value) => normalizeVariable(getTagVariable(option)) === normalizeVariable(getTagVariable(value))}
                  value={selectedTag}
                  onChange={handleTagSelection}
                  freeSolo
                  inputValue={newTagVariable}
                  onInputChange={(event, newInputValue, reason) => {
                    setNewTagVariable(newInputValue);
                    if (
                      reason === 'input'
                      && selectedTag
                      && normalizeVariable(getTagVariable(selectedTag)) !== normalizeVariable(newInputValue)
                    ) {
                      setSelectedTag(null);
                      setNewTagDescription('');
                      setNewTagIcon('');
                    }
                    if (createTagErrors.variable) {
                      setCreateTagErrors(prev => ({ ...prev, variable: '' }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}                      placeholder={t('add_tag_dialog.search_or_create_tag_placeholder') || 'Type to search or create new tag...'}
                      error={!!createTagErrors.variable}
                      helperText={createTagErrors.variable || (t('add_tag_dialog.select_existing_or_create_new') || 'Select an existing tag or type a new name')}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: 'white',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
                          },
                          '&.Mui-focused': {
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.25)',
                            borderColor: '#5757b0',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#666',
                          '&.Mui-focused': {
                            color: '#5757b0',
                          }
                        }
                      }}
                    />
                  )}                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box 
                        key={key}
                        component="li" 
                        {...optionProps}
                        sx={{
                          p: 2,
                          borderRadius: '8px',
                          margin: '4px 8px',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(87, 87, 176, 0.1)',
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '8px',
                              background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                              flexShrink: 0,
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                              {getTagVariable(option).charAt(0).toUpperCase()}
                            </Typography>
                          </Box>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="body1" fontWeight="medium" sx={{ color: '#333' }}>
                              {getTagVariable(option)}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              noWrap
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  }}
                  sx={{ 
                    '& .MuiAutocomplete-listbox': {
                      padding: '8px',
                    },
                    '& .MuiAutocomplete-paper': {
                      borderRadius: '16px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: '1px solid rgba(87, 87, 176, 0.1)',
                    }
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 1.5 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 1, 
                    color: '#2c2c54',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    '&::before': {
                      content: '""',
                      width: '3px',
                      height: '14px',
                      backgroundColor: '#5757b0',
                      borderRadius: '2px',
                      marginRight: '8px',
                    }
                  }}                >
                  {t('add_tag_dialog.description') || 'Description'}
                </Typography>
                <TextField
                  placeholder={t('add_tag_dialog.describe_tag_placeholder') || 'Describe what this tag is used for...'}
                  value={newTagDescription}
                  onChange={(e) => {
                    if (!selectedTag) {
                      setNewTagDescription(e.target.value);
                      if (createTagErrors.description) {
                        setCreateTagErrors(prev => ({ ...prev, description: '' }));
                      }
                    }
                  }}
                  fullWidth
                  multiline
                  rows={2}
                  error={!!createTagErrors.description}                  helperText={
                    selectedTag 
                      ? (t('add_tag_dialog.description_from_existing_readonly') || '✓ Description from existing tag (read-only)') 
                      : createTagErrors.description || (t('add_tag_dialog.describe_tag_helper') || 'Describe what this tag is used for')
                  }
                  required
                  variant="outlined"
                  disabled={!!selectedTag}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: selectedTag ? 'rgba(245, 245, 245, 0.8)' : 'white',
                      transition: 'all 0.3s ease',
                      '&:hover:not(.Mui-disabled)': {
                        boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(87, 87, 176, 0.25)',
                        borderColor: '#5757b0',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(87, 87, 176, 0.05)',
                        border: '1px dashed rgba(87, 87, 176, 0.3)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#666',
                      '&.Mui-focused': {
                        color: '#5757b0',
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      display: 'flex',
                      alignItems: 'center',
                      color: selectedTag ? '#4caf50' : 'inherit',
                    }
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 1.5 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 1, 
                    color: '#2c2c54',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    '&::before': {
                      content: '""',
                      width: '3px',
                      height: '14px',
                      backgroundColor: '#5757b0',
                      borderRadius: '2px',
                      marginRight: '8px',
                    }
                  }}
                >
                  {t('add_tag_dialog.icon') || 'Icon (Optional)'}
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 1,
                  p: 2,
                  border: '1px solid rgba(87, 87, 176, 0.2)',
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  minHeight: '60px',
                  alignItems: 'center'
                }}>
                  {/* No icon option */}
                  <Box
                    onClick={() => setNewTagIcon('')}
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      border: newTagIcon === '' ? '2px solid #5757b0' : '1px solid #ddd',
                      backgroundColor: newTagIcon === '' ? 'rgba(87, 87, 176, 0.1)' : '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '10px' }}>
                      {t('add_tag_dialog.no_icon', 'None')}
                    </Typography>
                  </Box>
                  
                  {/* Available icons */}
                  {availableIcons.slice(1).map((iconFile) => (
                    <Box
                      key={iconFile}
                      onClick={() => setNewTagIcon(iconFile)}
                      sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        border: newTagIcon === iconFile ? '2px solid #5757b0' : '1px solid #ddd',
                        backgroundColor: newTagIcon === iconFile ? 'rgba(87, 87, 176, 0.1)' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }
                      }}
                    >
                      <img 
                        src={`/images/icons/${iconFile}`}
                        alt={iconFile}
                        style={{
                          width: '24px',
                          height: '24px',
                          objectFit: 'contain'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  ))}
                </Box>
                
                <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                  {t('add_tag_dialog.icon_helper') || 'Select an icon to display with this tag (optional)'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 1.5 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 'bold', 
                    mb: 1, 
                    color: '#2c2c54',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '0.95rem',
                    '&::before': {
                      content: '""',
                      width: '3px',
                      height: '14px',
                      backgroundColor: '#5757b0',
                      borderRadius: '2px',
                      marginRight: '8px',
                    }
                  }}                >
                  {t('add_tag_dialog.value') || 'Value'}
                </Typography>
                <TextField                  placeholder={t('add_tag_dialog.enter_tag_value_placeholder') || 'Enter the value for this tag...'}
                  value={newTagValue}
                  onChange={(e) => {
                    setNewTagValue(e.target.value);
                    if (createTagErrors.value) {
                      setCreateTagErrors(prev => ({ ...prev, value: '' }));
                    }
                  }}
                  fullWidth
                  error={!!createTagErrors.value}
                  helperText={createTagErrors.value || (t('add_tag_dialog.enter_tag_value_helper') || 'Enter the specific value for this tag instance')}
                  required
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(87, 87, 176, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(87, 87, 176, 0.25)',
                        borderColor: '#5757b0',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#666',
                      '&.Mui-focused': {
                        color: '#5757b0',
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 },
            py: 2,
            display: 'flex', 
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
            borderTop: '1px solid rgba(87, 87, 176, 0.1)',
            gap: 1.5
          }}
        >
          <Button 
            onClick={onClose}
            variant="outlined"
            size="medium"
            sx={{ 
              borderColor: 'rgba(87, 87, 176, 0.3)',
              color: primaryColor,
              '&:hover': { 
                backgroundColor: 'rgba(87, 87, 176, 0.05)',
                borderColor: primaryColor,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.2)',
              },
              py: 1,
              px: 3,
              borderRadius: '10px',
              fontWeight: 'medium',
              fontSize: '0.9rem',
              minWidth: '100px',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 44,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {t('add_tag_dialog.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleCreateNewTag}
            disabled={isCreating}
            variant="contained"
            size="medium"
            sx={{
              background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
              color: '#fff',
              '&:hover': { 
                background: 'linear-gradient(135deg, #6767c0 0%, #8787e0 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(87, 87, 176, 0.4)',
              },
              py: 1,
              px: 3,
              borderRadius: '10px',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              minWidth: '140px',
              width: { xs: '100%', sm: 'auto' },
              minHeight: 44,
              boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.5s',
              },
              '&:hover::before': {
                left: '100%',
              }
            }}
          >
            {selectedTag ? (t('add_tag_dialog.add_existing_tag') || '✨ Add Existing Tag') : (t('add_tag_dialog.create_new_tag') || '🏷️ Create New Tag')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddTagDialog;
